import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch } from '../../../Hooks/Hooks';
import { typeAheadCall } from '../../../Redux/Reducers/SearchSlice';
import { IAddress, TTypeAheadContainerProps } from './TypeAheadContainer.type';
import { debounce, isEmptyObject } from '../../../Utils/Basic';
import './TypeAheadContainer.scss';

const TypeAheadContainer = (props: TTypeAheadContainerProps) => {
  const dispatch = useAppDispatch();
  const TypeAheadInputRef = useRef<HTMLInputElement>(null);
  const InputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useContext(AppContext);
  const { type, data, handleLocationChange } = props;
  const [typeAheadLocationData, setTypeAheadLocationData] = useState<IAddress[]>([]);
  const [userTypedData, setUserTypedData] = useState('Anywhere');
  const getAddressValue = (value: IAddress | null) => {
    if (isEmptyObject(value) || value === null) return '';
    const { City, State } = value;
    let address = City || '';
    if (State) {
      address += `, ${State}`;
    }
    return address || 'Anywhere';
  };

  useEffect(() => {
    if (isEmptyObject(data) && userTypedData) {
      setUserTypedData('Anywhere');
    }
  }, [isEmptyObject(data)]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });
  const handleOutsideClick = (e: any) => {
    if (TypeAheadInputRef.current && !TypeAheadInputRef.current.contains(e.target)) {
      setTypeAheadLocationData([]);
      isEmptyObject(data) && setUserTypedData('Anywhere');
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    isEmptyObject(data) && setUserTypedData(value);
    callAPI({ query: value }, type);
  };
  const callAPI = useCallback(
    debounce((_value: any, _type: string) => fetchAddressList(_value, _type), 500),
    [],
  );

  const fetchAddressList = (value: any, type: string) => {
    if (value?.query?.length > 2) {
      const LocationCallback: any = (res: any) => {
        if (res?.Locations) {
          const locationData: IAddress[] = res?.Locations?.map((item: any) => {
            return {
              ...item?.Address,
              ...item?.Coords,
              TimeZone: item?.TimeZone,
            };
          });
          setTypeAheadLocationData(locationData);
        }
        if (res?.ErrString) {
          //toast
        }
      };
      return dispatch(
        typeAheadCall({
          queryPayloads: value,
          callback: LocationCallback,
        }),
      );
    }
  };
  useEffect(() => {
    const handleFocus = () => {
      if (isMobile) {
        document.body.classList.add('Keyboard_Open');
      }
    };
    const handleBlur = () => {
      if (isMobile) {
        document.body.classList.remove('Keyboard_Open');
      }
    };
    const inputElement = InputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }

    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [isMobile, InputRef]);

  return (
    <div className="Type_Ahead_Container">
      <div className="Type_Ahead_Input_Container">
        <div className="Type_Ahead_Input" ref={TypeAheadInputRef}>
          {isMobile && <img src="/oo/assets/icon/search.svg" alt="Search icon" />}
          <input
            value={getAddressValue(data) || userTypedData}
            onChange={handleChange}
            onFocus={() => {
              setTypeAheadLocationData([]);
              setUserTypedData('');
              handleLocationChange(type, {});
            }}
            ref={InputRef}
            id={`Type_Ahead_Input ${type}`}
            autoComplete="off"
          />
          {typeAheadLocationData?.length > 0 && (
            <div className="Location_List">
              {typeAheadLocationData?.map((item: any, index: number) => {
                return (
                  <div
                    key={item?.City + item?.State + index}
                    onClick={() => {
                      handleLocationChange(type, item);
                      setTypeAheadLocationData([]);
                    }}
                  >
                    {item?.City}, {item?.State}
                  </div>
                );
              })}
            </div>
          )}
          {!isMobile && (
            <img
              src="/oo/assets/icon/cancel.svg"
              alt="cancel icon"
              onClick={() => {
                setTypeAheadLocationData([]);
                setUserTypedData('');
                handleLocationChange(type, {});
                document?.getElementById(`Type_Ahead_Input ${type}`)?.focus();
              }}
            />
          )}
        </div>
        {!isMobile && (
          <div className="Anywhere_Selector ml-3">
            <img
              src={`/oo/assets/icon/${
                (getAddressValue(data) || userTypedData) === 'Anywhere' ? 'apply' : 'white-button'
              }.svg`}
              alt="anywhere selector icon"
              onClick={() => {
                setTypeAheadLocationData([]);
                setUserTypedData('Anywhere');
                handleLocationChange(type, {});
              }}
            />
            <span>Anywhere</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeAheadContainer;
